import Cookies from "universal-cookie";

const COOKIE_NAME = "acp_utm_params";
const LEGACY_COOKIE_NAME = "acp_utm_param";

function readUtmCookie(cookies: Cookies): Record<string, string> | null {
  const current = cookies.get(COOKIE_NAME);
  if (current && typeof current === "object") return current;
  const legacy = cookies.get(LEGACY_COOKIE_NAME);
  if (legacy && typeof legacy === "object") return legacy;
  return null;
}

/**
 * Captures UTM parameters from URL and stores in cookie.
 * Returns the UTM data as a JSON string, or null if no UTM data is found.
 *
 * Priority: URL parameters > existing cookie
 * Storage: acp_utm_params cookie on .acp-usa.org (30-day expiration).
 * The cookie name matches www.acp-usa.org's WordPress theme so attribution
 * captured upstream is visible here even when outbound link enhancement
 * doesn't carry the UTMs through (typed URLs, bookmarks, etc.).
 * The legacy singular name acp_utm_param is also read as a fallback for
 * cookies set by earlier versions of this app.
 *
 * Automatically strips UTM params from URL after capturing to keep URLs clean.
 */
export function captureUtmParameters(): string | null {
  try {
    const cookies = new Cookies();

    // Try URL parameters first
    const urlParams = new URLSearchParams(window.location.search);
    const utmData: Record<string, string> = {};
    const cleanParams = new URLSearchParams();

    let hasUtmParams = false;
    urlParams.forEach((value, key) => {
      if (key.startsWith("utm_")) {
        utmData[key] = value;
        hasUtmParams = true;
      } else {
        // Keep non-UTM params for clean URL
        cleanParams.set(key, value);
      }
    });

    if (hasUtmParams) {
      // Determine the appropriate cookie domain based on hostname
      const hostname = window.location.hostname;
      let cookieDomain: string | undefined;
      if (hostname.endsWith(".acp-usa.org") || hostname === "acp-usa.org") {
        cookieDomain = ".acp-usa.org";
      } else if (hostname.endsWith(".tim.bz") || hostname === "tim.bz") {
        cookieDomain = ".tim.bz";
      }
      // Otherwise leave undefined to use current hostname

      // Set cookie with 30-day expiration
      cookies.set(COOKIE_NAME, utmData, {
        path: "/",
        ...(cookieDomain && { domain: cookieDomain }),
        maxAge: 30 * 24 * 60 * 60, // 30 days in seconds
        secure: true,
        sameSite: "lax",
      });

      // Strip UTM params from URL to keep it clean
      const cleanUrl = cleanParams.toString()
        ? `${window.location.pathname}?${cleanParams.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, "", cleanUrl);

      return JSON.stringify(utmData);
    }

    // No URL params - check existing cookie (current name, then legacy)
    const cookieData = readUtmCookie(cookies);
    if (cookieData) {
      return JSON.stringify(cookieData);
    }

    return null;
  } catch (error) {
    console.error("Error capturing UTM parameters:", error);
    return null;
  }
}

/**
 * Gets previously captured UTM data from cookie
 */
export function getUtmData(): string | null {
  try {
    const cookies = new Cookies();
    const cookieData = readUtmCookie(cookies);
    if (cookieData) {
      return JSON.stringify(cookieData);
    }
    return null;
  } catch (error) {
    console.error("Error retrieving UTM data:", error);
    return null;
  }
}
